import { forwardRef, type ComponentPropsWithoutRef, type ElementType, type HTMLAttributes, type ReactNode } from "react";
import { Screen, type ScreenProps } from "../Screen";
import s from "./FloatingTabBar.module.css";

function cx(...classes: (string | false | undefined)[]): string {
  return classes.filter(Boolean).join(" ");
}

export type FloatingTabBarShellProps = ScreenProps & {
  /**
   * Collapse the bar out of the way — for an on-screen keyboard, which covers
   * the pill anyway, so holding its footprint only costs the content above it.
   * Zeroes `--tab-bar-space`, so whatever reserved that padding gets it back
   * and its own bottom-pinned content drops with it.
   *
   * Pass the same value to the `FloatingTabBar` inside: the shell owns the
   * geometry, the pill owns its own chrome and accessibility state. A shell
   * that collapses wants `overflow: hidden` in its own CSS, which is what
   * clips the pill as it slides past the bottom edge.
   */
  collapsed?: boolean;
};

/**
 * The app shell a `FloatingTabBar` floats inside. A `Screen` that is also the
 * bar's containing block and declares the bar's geometry — see
 * FloatingTabBar.module.css for the arithmetic and why it lives on this
 * element rather than at `:root`.
 *
 * The bar is out of flow, so whatever scrolls underneath it does not get its
 * height for free: give the scrolling element `padding-bottom:
 * var(--tab-bar-space)` and content will pass behind the pill while scrolling
 * but still clear it at the end.
 *
 * Layout, background and any hard `height` stay in the app's own CSS module,
 * composed on via `className` exactly as with `Screen`.
 */
export const FloatingTabBarShell = forwardRef<HTMLDivElement, FloatingTabBarShellProps>(function FloatingTabBarShell(
  { className, collapsed = false, ...rest },
  ref,
) {
  return <Screen ref={ref} className={cx(s.shell, collapsed && s.shellCollapsed, className)} {...rest} />;
});

export type FloatingTabBarProps = HTMLAttributes<HTMLElement> & {
  /** Slide the pill off the bottom edge. Off screen is not the same as gone,
      so this also takes it out of the accessibility tree and the tab order —
      nothing the user cannot see stays focusable. Pair it with the same prop
      on the shell, which is what gives the space back. */
  collapsed?: boolean;
};

/**
 * The pill itself. Renders a `<nav>`, and must be a child of
 * `FloatingTabBarShell` — it positions against it.
 */
export function FloatingTabBar({ className, collapsed = false, ...rest }: FloatingTabBarProps) {
  return (
    <nav
      className={cx(s.root, collapsed && s.collapsed, className)}
      {...rest}
      aria-hidden={collapsed || undefined}
      inert={collapsed}
    />
  );
}

interface FloatingTabOwnProps {
  /**
   * Icon slot, sized to a fixed box so every tab's label lands on the same
   * line. The slot is `position: relative`, so a consumer can hang its own
   * absolutely-positioned badge off it.
   */
  icon: ReactNode;
  label: ReactNode;
  className?: string;
}

export type FloatingTabProps<E extends ElementType> = FloatingTabOwnProps & {
  /** What the tab renders as — a router `NavLink` by default, `"button"` for
   * a tab that opens something instead of navigating. */
  as?: E;
} & Omit<ComponentPropsWithoutRef<E>, keyof FloatingTabOwnProps | "as" | "children">;

/**
 * One destination in the bar. The active state keys off `aria-current="page"`,
 * which react-router's `NavLink` sets for itself, so a link tab needs no
 * active class of its own.
 */
export function FloatingTab<E extends ElementType = "a">({
  as,
  icon,
  label,
  className,
  ...rest
}: FloatingTabProps<E>) {
  const Element = (as ?? "a") as ElementType;

  return (
    <Element className={cx(s.tab, className)} {...rest}>
      <span className={s.icon}>{icon}</span>
      <span className={s.label}>{label}</span>
    </Element>
  );
}
